/**
 * The three editable task fields — Status, Due date, Assignee — as canonical
 * `InlineEditableFieldRow`s: the same click-to-edit cell, pencil, clear, commit
 * and error handling every other slideout's overview uses.
 *
 * Shared by the task slideout's Overview and `TaskCard`, so a task edits the
 * same way wherever it appears. Where a value is written is the caller's
 * business — CRM events for a saved task, the proposal payload for a proposed
 * one — and withholding a save handler makes that row read-only.
 *
 * Renders the rows only, with no wrapper, so each surface owns its spacing and
 * can add rows of its own (the Overview appends Visibility and Last
 * interaction).
 */

import { Calendar, Flag01, User01 } from '@untitledui/icons';
import { Fragment, type ReactNode, useCallback } from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { DateTimeValueLabel } from '@/lib/crm/components/properties/labels/DateTimeValueLabel';
import { DateValueLabel } from '@/lib/crm/components/properties/labels/DateValueLabel';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { EmptyFieldPlaceholder } from '../components/EmptyFieldPlaceholder';
import { InlineEditableFieldRow } from '../components/InlineEditableFieldRow';
import { OverviewFieldRow } from '../components/OverviewFieldRow';
import {
  TASK_ENTITY_TYPE,
  TASK_STAGE_ASSOCIATION_TYPE_NAME,
} from './task.constants';
import type { TaskFieldKey } from './taskFieldOrder';
import { TaskStageLink } from './TaskStage';

/**
 * Value-slot classes, identical to the generic OverviewSection's rows: badge
 * and pill rows reserve the 22px chip line, property rows the text line.
 */
const PICKER_VALUE_CLASS = 'flex flex-1 flex-wrap gap-1 min-h-[22px]';
const PROPERTY_VALUE_CLASS =
  'text-xs md:text-sm font-medium text-fg-primary flex-1';

function toStageId(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function toTargetIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (id): id is string => typeof id === 'string' && id.length > 0
    );
  }
  return typeof value === 'string' && value.length > 0 ? [value] : [];
}

export interface TaskFieldRowsProps {
  order: readonly TaskFieldKey[];
  orgId: number;
  /** Empty string for a task that doesn't exist yet (a proposed one). */
  taskEntityId: string;
  stageEntityId: string | null;
  ownerEntityId: string | null;
  date?: string;
  datetime?: string;
  /** The `ahoy_date` schema; undefined while it loads (row is read-only). */
  dueDateSchema: PropertyTypeSchema | undefined;
  /** The task→owner association type; undefined while it loads. */
  assigneeAssociationType: AssociationType | undefined;
  onStageChange?: (
    taskEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
  onSaveDue?: (newDate: string | null) => Promise<void>;
  onSaveAssignee?: (targetIds: string[]) => Promise<void>;
  /**
   * True while another surface is writing the assignee (the slideout footer's
   * Reassign shares the mutation — TaskAssigneeMutationContext). The row stops
   * offering its editor until that write settles.
   */
  isAssigneeMutationPending?: boolean;
  /**
   * Whether the assignee may be CLEARED (the ✕ affordance). Defaults to true
   * for saved tasks. A proposed task models its owner as an
   * `ahoy_association_added` action the proposal editor can't remove, so
   * clearing would write an empty (invalid) target — that caller passes false.
   */
  allowAssigneeClear?: boolean;
}

export function TaskFieldRows({
  order,
  orgId,
  taskEntityId,
  stageEntityId,
  ownerEntityId,
  date,
  datetime,
  dueDateSchema,
  assigneeAssociationType,
  onStageChange,
  onSaveDue,
  onSaveAssignee,
  isAssigneeMutationPending = false,
  allowAssigneeClear = true,
}: TaskFieldRowsProps) {
  // The date editor is date-only; a task carrying only a datetime seeds it
  // from the datetime's calendar date so it never opens empty.
  const dueValue = date ?? (datetime ? datetime.split('T')[0] : null);

  const canEditStage = onStageChange !== undefined;
  const canEditDue = onSaveDue !== undefined && dueDateSchema !== undefined;
  const canEditAssignee =
    onSaveAssignee !== undefined &&
    assigneeAssociationType !== undefined &&
    !isAssigneeMutationPending;

  const handleSaveStage = useCallback(
    async (value: unknown) => {
      await onStageChange?.(taskEntityId, toStageId(value));
    },
    [onStageChange, taskEntityId]
  );

  const handleSaveDue = useCallback(
    async (value: unknown) => {
      await onSaveDue?.(typeof value === 'string' && value ? value : null);
    },
    [onSaveDue]
  );
  const handleClearDue = useCallback(async () => {
    await onSaveDue?.(null);
  }, [onSaveDue]);

  const handleSaveAssignee = useCallback(
    async (value: unknown) => {
      await onSaveAssignee?.(toTargetIds(value));
    },
    [onSaveAssignee]
  );
  const handleClearAssignee = useCallback(async () => {
    await onSaveAssignee?.([]);
  }, [onSaveAssignee]);

  let dueDisplay: ReactNode;
  if (datetime) {
    dueDisplay = <DateTimeValueLabel value={datetime} />;
  } else if (date) {
    dueDisplay = <DateValueLabel value={date} />;
  } else {
    dueDisplay = (
      <EmptyFieldPlaceholder label='Due date' isEditable={canEditDue} />
    );
  }

  const assigneeDisplay = ownerEntityId ? (
    <EntityBadgeById entityId={ownerEntityId} orgId={orgId} avatar />
  ) : (
    <EmptyFieldPlaceholder label='Assignee' isEditable={canEditAssignee} />
  );

  const rows: Record<TaskFieldKey, ReactNode> = {
    status: (
      <InlineEditableFieldRow
        kind='stage'
        icon={Flag01}
        label='Status'
        value={stageEntityId}
        onSave={handleSaveStage}
        isEditable={canEditStage}
        entityTypeName={TASK_ENTITY_TYPE}
        associationTypeName={TASK_STAGE_ASSOCIATION_TYPE_NAME}
        valueClassName={PICKER_VALUE_CLASS}
        // Editing an existing task's status: never auto-pick (and commit-blur
        // save) the default pipeline's first stage the user didn't choose.
        disableAutoSelect
      >
        {stageEntityId ? (
          <TaskStageLink orgId={orgId} stageEntityId={stageEntityId} />
        ) : (
          <EmptyFieldPlaceholder label='Status' isEditable={canEditStage} />
        )}
      </InlineEditableFieldRow>
    ),
    dueDate: dueDateSchema ? (
      <InlineEditableFieldRow
        kind='property'
        icon={Calendar}
        label='Due date'
        value={dueValue}
        onSave={handleSaveDue}
        onClear={canEditDue && dueValue ? handleClearDue : undefined}
        isEditable={canEditDue}
        schema={dueDateSchema}
        orgId={orgId}
        valueClassName={PROPERTY_VALUE_CLASS}
      >
        {dueDisplay}
      </InlineEditableFieldRow>
    ) : (
      <OverviewFieldRow
        icon={Calendar}
        label='Due date'
        valueClassName={PROPERTY_VALUE_CLASS}
      >
        {dueDisplay}
      </OverviewFieldRow>
    ),
    assignee: assigneeAssociationType ? (
      <InlineEditableFieldRow
        kind='association'
        icon={User01}
        label='Assignee'
        value={ownerEntityId ? [ownerEntityId] : []}
        onSave={handleSaveAssignee}
        onClear={
          allowAssigneeClear && canEditAssignee && ownerEntityId
            ? handleClearAssignee
            : undefined
        }
        isEditable={canEditAssignee}
        associationType={assigneeAssociationType}
        orgId={orgId}
        sourceEntityId={taskEntityId || undefined}
        valueClassName={PICKER_VALUE_CLASS}
      >
        {assigneeDisplay}
      </InlineEditableFieldRow>
    ) : (
      <OverviewFieldRow
        icon={User01}
        label='Assignee'
        valueClassName={PICKER_VALUE_CLASS}
      >
        {assigneeDisplay}
      </OverviewFieldRow>
    ),
  };

  return order.map(key => <Fragment key={key}>{rows[key]}</Fragment>);
}
